import { Component, Input, Output, EventEmitter, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';

import { ItemField } from '~/core/services/item-fields/item-fields';
import { IdentifierType } from '~/core/enum/widgets.enum';
import { MenuType } from '~/core/constants/sport-display';
import { LanguageButtonComponent } from '~/components/language-button/language-button.component';
import { CrmInboxButtonComponent } from '~/components/crm-inbox-button/crm-inbox-button.component';
import { MenuToggleButtonComponent } from '~/components/menu-toggle-button/menu-toggle-button.component';

const BUTTON_MENU_TYPES: MenuType[] = [MenuType.DEPOSITS, MenuType.REGISTER];

const BUTTON_MENU_TYPE_FALLBACKS: Partial<Record<MenuType, string>> = {
  [MenuType.DEPOSITS]: 'GENERAL.DEPOSIT_2',
  [MenuType.REGISTER]: 'GENERAL.REGISTER_2',
};

@Component({
  selector: 'app-menu-top-bar-item',
  templateUrl: './menu-top-bar-item.component.html',
  styleUrls: ['./menu-top-bar-item.component.scss'],
  standalone: true,
  host: { '[class.in-subheader]': 'inSubheader()' },
  imports: [
    CommonModule,
    RouterLink,
    MatButton,
    MatIcon,
    MatTooltip,
    LanguageButtonComponent,
    TranslatePipe,
    CrmInboxButtonComponent,
    MenuToggleButtonComponent,
  ],
})
export class MenuTopBarItemComponent {
  /** The config-driven item to render. */
  readonly item = input.required<ItemField>();

  /** Whether a casino/casino-live page is currently open (hides theme button). */
  @Input() isCasinoOpened: boolean = false;

  /** Current theme — true if light theme is active. */
  @Input() isLightTheme: boolean = false;

  /** Whether the user is logged in (used for CONFIG condition). */
  @Input() isLoggedIn: boolean = false;

  /**
   * Whether to show a matTooltip on the theme button.
   * Desktop uses a tooltip; mobile does not.
   */
  @Input() showThemeTooltip: boolean = true;

  /**
   * Whether to hide the light-mode button.
   * Mirrors `themeService.hideButtonLightMode()`.
   */
  @Input() hideButtonLightMode: boolean = false;

  /** CSS class to apply to text labels (differs slightly between desktop and mobile). */
  @Input() labelClass: string = 'menu-top-bar__option-label';

  /** Whether this item is currently active (matching the current route). */
  @Input() isActive: boolean = false;

  /** Internal router path for this item. When set, the item renders as an <a> with routerLink. */
  readonly routerPath = input<string | null>(null);

  /** Whether the CRM inbox button should be shown (requires company config). */
  @Input() showCrmButton: boolean = false;

  /** Controls how the item is rendered: inline in the bar or as a list row in the overflow dropdown. */
  @Input() displayMode: 'bar' | 'list' = 'bar';

  /** When true, caps the image max-height to 24px (subheader rows have limited height). */
  readonly inSubheader = input(false);

  /** Emitted when a generic item is clicked (default / image+title case). */
  @Output() itemClick = new EventEmitter<ItemField>();

  /** Emitted when the theme toggle button is clicked. */
  @Output() themeToggle = new EventEmitter<void>();

  readonly IdentifierType = IdentifierType;
  readonly MenuType = MenuType;

  readonly _badgeBgColor = computed(() => {
    const badge = this.item().view?.badge;
    return badge?.enable ? badge.background_color || 'var(--fills-theme-default)' : '';
  });

  readonly _badgeTextColor = computed(() => {
    const badge = this.item().view?.badge;
    return badge?.enable ? badge.text_color || 'var(--text-theme-on-theme)' : '';
  });

  readonly _isButtonItem = computed(() => {
    const item = this.item();
    return item.identifier_type === IdentifierType.MENU_TYPE && BUTTON_MENU_TYPES.includes(item.action?.menu_type as MenuType);
  });

  readonly _buttonLabel = computed(() => {
    const item = this.item();
    const menuType = item.action?.menu_type as MenuType;
    return item.view?.title || BUTTON_MENU_TYPE_FALLBACKS[menuType] || '';
  });
}
